import { User } from "@/lib/types";
import { UserEmail } from "./UserEmail";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { useEffect, useMemo, useRef, useState } from "react";
import { request } from "@/lib/request";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  CheckIcon,
  Loader2Icon,
  SaveIcon,
  Trash2Icon,
  UploadIcon,
  UserIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useUser } from "@/hooks/useUserProvider";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useFileSignedUrlMap } from "@/hooks/useFileSignedUrlMap";

const formSchema = z.object({
  nickname: z.string().default("").nullable(),
});

type UploadFileResponse = {
  data: {
    id: string;
  };
};

export const UserAccount = ({ user }: { user: User }) => {
  const { syncUser } = useUser();

  const successTimerRef = useRef<NodeJS.Timeout | null>(null);
  const avatarInputRef = useRef<HTMLInputElement | null>(null);
  const [isSubmitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSuccess, setSuccess] = useState(false);
  const [isAvatarUploading, setAvatarUploading] = useState(false);
  const [isAvatarRemoving, setAvatarRemoving] = useState(false);
  const [avatarError, setAvatarError] = useState<string | null>(null);

  const avatarFileIds = useMemo(
    () => (user.avatarFileId ? [user.avatarFileId] : []),
    [user.avatarFileId],
  );

  const { signedUrlByFileId } = useFileSignedUrlMap({
    fileIds: avatarFileIds,
    enabled: !!user.avatarFileId,
  });

  const avatarUrl = user.avatarFileId
    ? signedUrlByFileId[user.avatarFileId] || null
    : null;

  const isAvatarBusy = isAvatarUploading || isAvatarRemoving;

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      nickname: user.nickname,
    },
  });

  const handleSubmit = async (data: z.infer<typeof formSchema>) => {
    setError(null);
    setSubmitting(true);
    try {
      await request
        .put(`/user/info`, {
          nickname: data.nickname,
        })
        .catch(err => {
          throw new Error(
            err.response?.data.message
              ? err.response?.data.message
              : "Something went wrong, please try again.",
          );
        });

      await syncUser();
      setSuccess(true);
      successTimerRef.current = setTimeout(() => {
        setSuccess(false);
      }, 5 * 1000);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "An unknown error occurred.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const updateAvatar = async (avatarFileId: string | null) => {
    await request
      .put(`/user/info`, {
        avatarFileId,
      })
      .catch(err => {
        throw new Error(
          err.response?.data.message
            ? err.response?.data.message
            : "Failed to update avatar, please try again.",
        );
      });

    await syncUser();
  };

  const onAvatarInputChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file) {
      return;
    }

    setAvatarError(null);
    setAvatarUploading(true);

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("usage", "user_avatar");
      formData.append("visibility", "private");

      const response = await request.post<UploadFileResponse>(
        "/files",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      await updateAvatar(response.data.data.id);
    } catch (err) {
      setAvatarError(
        err instanceof Error ? err.message : "Failed to update avatar.",
      );
    } finally {
      setAvatarUploading(false);
    }
  };

  const handleRemoveAvatar = async () => {
    if (!user.avatarFileId) {
      return;
    }

    setAvatarError(null);
    setAvatarRemoving(true);

    try {
      await updateAvatar(null);
    } catch (err) {
      setAvatarError(
        err instanceof Error ? err.message : "Failed to remove avatar.",
      );
    } finally {
      setAvatarRemoving(false);
    }
  };

  useEffect(() => {
    return () => {
      if (successTimerRef.current) {
        clearTimeout(successTimerRef.current);
      }
    };
  }, []);

  return (
    <>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(handleSubmit)}
          className="flex flex-col gap-4"
        >
          <div className="flex items-center justify-between gap-4">
            <span className="w-[100px] text-sm font-bold">Avatar</span>
            <div className="flex max-w-[300px] items-center justify-end gap-2">
              <Avatar className="border-border/60 size-12 border">
                {avatarUrl ? (
                  <AvatarImage
                    src={avatarUrl}
                    alt={`${user.displayName} avatar`}
                    className="object-cover"
                  />
                ) : null}
                <AvatarFallback>
                  <UserIcon className="text-muted-foreground size-4" />
                </AvatarFallback>
              </Avatar>
              <input
                ref={avatarInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={onAvatarInputChange}
              />
              <Button
                type="button"
                size="sm"
                variant="secondary"
                disabled={isAvatarBusy || isSubmitting}
                onClick={() => avatarInputRef.current?.click()}
              >
                {isAvatarUploading ? (
                  <Loader2Icon className="size-4 animate-spin" />
                ) : (
                  <UploadIcon className="size-4" />
                )}
                Upload
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                disabled={!user.avatarFileId || isAvatarBusy || isSubmitting}
                onClick={handleRemoveAvatar}
                className="text-destructive hover:bg-destructive/10 hover:text-destructive"
              >
                {isAvatarRemoving ? (
                  <Loader2Icon className="size-4 animate-spin" />
                ) : (
                  <Trash2Icon className="size-4" />
                )}
                Remove
              </Button>
            </div>
          </div>
          <FormField
            control={form.control}
            name="nickname"
            render={({ field }) => (
              <FormItem className="flex items-center justify-between gap-4 space-y-0">
                <FormLabel className="w-[100px] font-bold">Name</FormLabel>
                <FormControl>
                  <Input
                    placeholder="Name"
                    className="max-w-[300px]"
                    value={field.value || ""}
                    onChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <UserEmail user={user} />
          <p className="text-muted-foreground text-xs">
            Password, signed-in devices and account activity are under Security.
          </p>
          {avatarError ? (
            <Alert variant="destructive">
              <AlertTitle className="font-bold">
                Could not update avatar
              </AlertTitle>
              <AlertDescription>{avatarError}</AlertDescription>
            </Alert>
          ) : null}
          {error ? (
            <Alert variant="destructive">
              <AlertTitle className="font-bold">Could not save</AlertTitle>
              <AlertDescription>
                {error ? error : "An unknown error occurred."}
              </AlertDescription>
            </Alert>
          ) : null}
          <div className="flex justify-end">
            <Button
              variant="default"
              size="sm"
              disabled={isSubmitting || isSuccess}
              className={cn({
                "text-green-500 disabled:bg-white disabled:opacity-100":
                  isSuccess,
              })}
            >
              {isSuccess ? (
                <>
                  <CheckIcon className="text-green-500" />
                  Saved
                </>
              ) : isSubmitting ? (
                <>
                  <Loader2Icon className="animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <SaveIcon />
                  Submit Changes
                </>
              )}
            </Button>
          </div>
        </form>
      </Form>
    </>
  );
};
